import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import "./landing.css";
import { HeroCanvas } from "./HeroCanvas";
import { Nav, TrustStrip, SectionCards, SectionTrace, SectionSteps, SectionPractices, CtaBand, Footer } from "./sections";
import { fetchLandingComps } from "../lib/landingApi";
import { useSession } from "../lib/useSession";
import { RequestAccessLink } from "../ui/RequestAccessLink";
import type { LandingComp } from "./HeroCanvas.engine";
import { useDocumentTitle } from "../ui/useDocumentTitle";

/**
 * The `/next` landing page — folded into the existing Vite/React client per the owner's explicit
 * choice (session, 2026-07-26) over the implementation guide's Next.js/RSC/Drizzle architecture.
 * "Same brains, two skins" doesn't apply here (there's no backend engine work on this page beyond
 * the one new read-only comps endpoint) — what carries over from the guide is the CONTENT spec:
 * real comp data, the hero behavior, and the accessibility/SEO checklist (§8), adapted to a
 * single-bundle SPA rather than an RSC-static + one client-island split.
 */
export function LandingPage() {
  const [comps, setComps] = useState<LandingComp[]>([]);
  // Who, if anyone, is signed in. The nav asks the same hook, so both surfaces answer from one
  // round trip and cannot contradict each other — which is exactly how they came to disagree.
  const session = useSession();
  // Read here, with the other hooks, because the redirect below is conditional and a hook may not
  // be.
  const [params] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    fetchLandingComps().then((c) => {
      if (!cancelled) setComps(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // VB-39: was a bare effect here; the shared hook restores the previous title on unmount so a
  // route change never leaves the landing's name on an app screen.
  useDocumentTitle("ValuBrief — every number traces to its source");

  /* ============================================================================================
     A SIGNED-IN VISITOR BELONGS IN THE APP  (S03, 2026-09-22)
     ============================================================================================
     Signed OUT, `/` is the marketing page and that is correct and unchanged. Signed IN, landing on
     the sales pitch for a product you already pay for is the same class of defect the 2026-08-01
     auth-aware nav fixed one layer up: the page was still addressing a stranger. `/app` is the
     signed-in front door, so that is where `/` sends a member.

     `?site=1` IS THE WAY BACK, AND IT HAS TO EXIST. A redirect with no escape would make
     valubrief.ai unreachable from inside the product, which is its own dead end — the footer of
     `/app` links here with that parameter and this check honours it. A query parameter rather than
     a second route, so `/` stays the one canonical marketing address for everyone signed out.

     ONLY ON "in". `useSession` is three-valued on purpose and "unknown" is the first paint: the
     marketing page is the right thing to render for a visitor we have not identified yet, and
     rendering nothing while we ask is the blank-page defect `auth/RequireAuth` documents. So a
     member does see this page for the length of one `/api/me` round trip before being moved. That
     is measured and accepted here rather than hidden — the alternative is a blank first paint for
     everybody, including the visitors this page is for.
     ============================================================================================ */
  if (session.status === "in" && params.get("site") !== "1") {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="vb-landing">
      <Nav />
      <header className="hero" id="top">
        <HeroCanvas comps={comps} />
        <div className="hero-veil" aria-hidden="true" />
        <div className="hero-in">
          <span className="pill">Source-traced · never invented</span>
          <h1>
            Valuation reports where every number <em>traces to its source.</em>
          </h1>
          {/* THE LEDE NAMED THREE THINGS AND NOT ONE OF THEM WAS OPENABLE (2026-09-04).
              The 09-01 pass fixed a real defect — the lede had been residential-first with the other
              two verticals collapsed into a trailing clause — and replaced it with all three
              verticals named co-equally. Correct about the architecture, and it left the first
              paragraph on the site naming property-tax protest, BPO and appraisal support while
              saying nothing about the CMA, the Advanced Market Brief or the CRM, which are the ONLY
              things a visitor can actually open. `APPRAISAL_SUPPORT_ENABLED` is false, and no
              protest route exists in App.tsx at all.

              A page whose headline is that a number must trace to its source cannot open by listing
              three deliverables you cannot reach. So the lede leads with what is live and names what
              is coming, in that order. The three verticals are still the architecture and still get
              equal billing at #who — this sentence is about AVAILABILITY, which is a different fact.

              Checked against `flags.ts` and the route table, not against anyone's intention: if the
              flag flips, this sentence changes with it. */}
          <p className="lede">
            One source-traced engine. Today it runs the CMA, the Advanced Market Brief and the CRM your records
            live in; property-tax protest and appraisal support are coming. Every figure carries the record it
            came from, or the words <em>you entered</em>.
          </p>
          {/* AUTH-AWARE HERO (2026-08-15). THE OTHER HALF OF THE 2026-08-01 NAV FIX.
              Owner, repeatedly: "its saying request access and sign in when I am already signed
              in ... its been a problem since the beginning".

              The nav was made auth-aware on 2026-08-01 and this block was not, so a signed-in
              member saw their own address and "Open the app" in the header while the hero directly
              beneath it still asked them to request access and sign in. The header was right and
              the hero was two inches below it contradicting it, which is worse than either alone.

              Same three-state rule the nav uses, and for the same reason (`lib/useSession`):
                in       -> Open the app. Nothing else; they are already a member.
                out      -> Request access + Sign in, exactly as before.
                unknown  -> the neutral affordance ONLY. Rendering "Sign in" during the round trip
                            is what flashes the wrong verb at someone who is already signed in. */}
          <div className="hero-cta" id="start">
            {session.status === "out" ? (
              <>
                <RequestAccessLink />
                <a className="btn quiet" href="/login">
                  Sign in
                </a>
              </>
            ) : (
              <a className="btn" href="/app">
                Open the app
              </a>
            )}
          </div>
          {/* THE PRICE CAME OUT (A-MARKETING, 2026-09-01). UI-027 put commercial terms here to catch a
              CoStar/Ownwell-burned prospect: "Free during the pilot — no card, no contract, no
              auto-renewal." Four commercial commitments, and not one of them resolves to a file in
              this repo — there is no billing surface in this client to read them out of, and every
              dollar figure in this project's knowledge is recorded as reference-only and unratified.
              A page whose headline is that a number must trace to its source cannot make four
              contract promises out of nothing. They are removed rather than reworded.

              What is left is what `RequestAccessLink` and `useSignups` actually know: sign-ups are
              closed, so the button goes to /login and access is by request. "statewide coverage in
              progress" is also gone — that was a roadmap claim; the two data layers are named
              instead, which is what the 2026-07-26 Texas reframe in sections.tsx already settled on. */}
          {/* "with Cotality where a county is not loaded" was cut 2026-10-20 (owner decision 2, item 1F):
              the connector is dormant, so the claim was untrue and the name may not appear. */}
          <ul className="hero-who" aria-label="Who it is for">
            <li>
              <a href="#who">Property-tax consultants</a>
            </li>
            <li>
              <a href="#who">Brokers &amp; agents</a>
            </li>
            <li>
              <a href="#who">Appraisers</a>
            </li>
          </ul>
          <p className="hero-note">
            For licensed real-estate professionals. Access is by request. Texas first — county appraisal district
            records.
          </p>
        </div>
        {/* THE HERO LEGEND WAS REMOVED (2026-08-10, owner). It read "◦ parcels — comp links
            ◎ comp lens" and it was a key to a picture that is not a chart.

            `HeroCanvas.engine.ts` states the rule it broke, in its own deviation note: when
            `next_landing_comps` is empty the engine renders the geometry but omits every number and
            the "N COMPS IN LENS / SUPPORTED RANGE" readout, because "a labeled-in-code fake number
            is still an invented number on the rendered page". The legend then told a visitor that
            those dots ARE parcels and those lines ARE comp links — the same data claim by another
            route, and made about an empty set, since /api/next/landing/comps returns no rows in
            production today.

            The animation stands as an illustration. If the legend ever comes back it belongs
            behind `comps.length > 0`, so it appears only alongside the readout it describes. */}
      </header>

      <TrustStrip />
      <SectionTrace />
      <SectionCards />
      <SectionSteps />
      <SectionPractices />
      <CtaBand />
      <Footer />
    </div>
  );
}
